import { useId, useState } from 'react';
import type { TFunction } from 'i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import styles from './UnitChoiceNote.module.css';

export interface IUnitChoiceNoteProps {
  material: string;
  /** The unit the site counts this material in — the row's locked unit. */
  stockUnit: string;
  /** The unit a stored line was saved in before it was switched to the
   *  stock's (`IDeliveryLineDraft.unitWas`). */
  unitWas?: string;
  quantity: string;
  /** "Count it in another unit" — the unit is changed on the stock record (a
   *  recount), never on one delivery. Absent ⇒ the option has no way out. */
  onCountInOtherUnit?: (material: string) => void;
  t: TFunction;
}

/**
 * Under a delivery line whose material is counted on site: why the unit is
 * locked, and the way out for a deliberate change (QA 2026-10-18 — a delivery
 * edited to kg was summed into a count in bags). A stored line that was saved
 * in another unit says so, and asks for its amount to be re-checked — it is
 * never converted, since no factor exists between the two.
 */
export function UnitChoiceNote({ material, stockUnit, unitWas, quantity, onCountInOtherUnit, t }: IUnitChoiceNoteProps) {
  /** Null while the box is closed; Keep opens selected. */
  const [choice, setChoice] = useState<'keep' | 'other' | null>(null);
  const radioName = useId();
  const unit = unitLabel(t, stockUnit);
  return (
    <>
      {unitWas ? (
        <span className={styles.warn} role="status">
          {t('logistics.orderDialog.unitSwitched', { qty: quantity, was: unitLabel(t, unitWas), material, unit })}
        </span>
      ) : null}
      {choice !== null ? (
        <div className={styles.choice} role="radiogroup" aria-label={t('logistics.orderDialog.unitCountedIn', { material, unit })}>
          <span className={styles.choiceTitle}>{t('logistics.orderDialog.unitCountedIn', { material, unit })}</span>
          <label className={styles.option}>
            {/* onClick, not onChange: choosing Keep again is how the box is put
                away, and a checked radio fires no change. */}
            <input type="radio" name={radioName} checked={choice === 'keep'} readOnly onClick={() => setChoice(null)} />
            <span>
              {t('logistics.orderDialog.unitKeep', { unit })}
              <span className={styles.optionHint}>{t('logistics.orderDialog.unitKeepHint', { unit })}</span>
            </span>
          </label>
          <label className={styles.option}>
            <input type="radio" name={radioName} checked={choice === 'other'} onChange={() => setChoice('other')} />
            <span>
              {t('logistics.orderDialog.unitOther', { material })}
              <span className={styles.optionHint}>{t('logistics.orderDialog.unitOtherHint', { material })}</span>
            </span>
          </label>
          {onCountInOtherUnit ? (
            <button type="button" className={styles.link} onClick={() => onCountInOtherUnit(material)}>
              {t('logistics.orderDialog.unitOpenStock', { material })}
            </button>
          ) : null}
        </div>
      ) : (
        <span className={styles.note}>
          {t('logistics.orderDialog.unitCountedNote', { material, unit })}{' '}
          <button type="button" className={styles.link} onClick={() => setChoice('keep')}>
            {t('logistics.orderDialog.unitNeedOther')}
          </button>
        </span>
      )}
    </>
  );
}
